/**
 * REST API client.
 *
 * Authentication is `httpOnly` cookie based (set by the hub on /login or
 * /signup). For state-changing requests (POST/PATCH/DELETE) we attach a
 * CSRF token sourced from the `pcp_csrf` cookie which the hub sets after
 * a successful login. Reads (GET) need no token.
 */

const HUB_URL = (import.meta.env.VITE_HUB_URL as string | undefined) ?? "";

export class ApiError extends Error {
  status: number;
  code?: string;
  details?: unknown;
  constructor(status: number, message: string, code?: string, details?: unknown) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

function readCookie(name: string): string | null {
  const m = document.cookie.match(
    new RegExp("(?:^|; )" + name.replace(/[.$?*|{}()[\]\\/+^]/g, "\\$&") + "=([^;]*)"),
  );
  return m ? decodeURIComponent(m[1]) : null;
}

type Method = "GET" | "POST" | "PATCH" | "DELETE" | "PUT";

interface RequestOptions {
  method?: Method;
  body?: unknown;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

export async function api<T = unknown>(path: string, opts: RequestOptions = {}): Promise<T> {
  const method: Method = opts.method ?? "GET";
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...(opts.headers ?? {}),
  };

  if (opts.body !== undefined) headers["Content-Type"] = "application/json";

  if (method !== "GET") {
    const csrf = readCookie("pcp_csrf");
    if (csrf) headers["X-CSRF"] = csrf;
  }

  const url = path.startsWith("http") ? path : `${HUB_URL}${path}`;

  const res = await fetch(url, {
    method,
    headers,
    credentials: "include",
    signal: opts.signal,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });

  const text = await res.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }

  if (!res.ok) {
    const errBody = (data ?? {}) as { code?: string; message?: string };
    throw new ApiError(
      res.status,
      errBody.message ?? `Request failed (${res.status})`,
      errBody.code,
      data,
    );
  }
  return data as T;
}

export const HUB_BASE = HUB_URL;
